import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import AuthLayout from "../components/AuthLayout";
import { howToContent } from "../components/HowToUse";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Loader2, Eye, EyeOff, ArrowRight, ArrowLeft, Home, Building2, ListChecks } from "lucide-react";
import { toast } from "sonner";

const inputCls = "bg-black/40 border-white/15 h-11 text-white focus:ring-2 focus:ring-[#ff3b30]";

export default function Signup() {
  const [step, setStep] = useState(0); // 0 = role, 1 = explainer, 2 = form
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "", role: "", company: "", lender_email: "", license_number: "" });
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const { login } = useAuth();
  const navigate = useNavigate();

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const isLender = form.role === "lender";

  const pickRole = (role) => {
    setForm({ ...form, role });
    setStep(1);
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post("/auth/signup", form);
      login(data.token, data.user);
      localStorage.setItem("mpl_onboard", "1");
      toast.success(isLender ? "Welcome aboard — you're the hero now 🍭" : "You're in! Let's get you set up 🍭");
      navigate(data.user.role === "admin" ? "/lender" : "/dashboard");
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  /* ---------- Step 0: role ---------- */
  if (step === 0) {
    return (
      <AuthLayout>
        <div className="bg-[#141414]/80 backdrop-blur-2xl border border-white/10 rounded-2xl p-8 shadow-2xl">
          <h1 className="font-display text-3xl tracking-tight">First things first 🍭</h1>
          <p className="text-white/55 text-sm mt-2">Which one are you? Pick one and we'll show you exactly how it works.</p>

          <div className="mt-6 space-y-3">
            {[
              { v: "realtor", icon: Home, label: "I'm a Realtor", desc: "Send pre-approvals to your buyers with your thumbs." },
              { v: "lender", icon: Building2, label: "I'm a Lender", desc: "Let your realtors do the busywork — on your brand." },
            ].map((o) => {
              const Icon = o.icon;
              return (
                <button
                  key={o.v}
                  type="button"
                  onClick={() => pickRole(o.v)}
                  data-testid={`role-${o.v}`}
                  className="w-full text-left rounded-xl border border-white/12 bg-black/30 hover:border-[#ff3b30]/60 hover:bg-[#ff3b30]/5 transition-colors p-4 flex items-center gap-4 group"
                >
                  <div className="h-11 w-11 rounded-xl bg-[#ff3b30]/15 text-[#ff3b30] flex items-center justify-center shrink-0">
                    <Icon className="h-5 w-5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold">{o.label}</p>
                    <p className="text-xs text-white/50">{o.desc}</p>
                  </div>
                  <ArrowRight className="h-5 w-5 text-white/30 group-hover:text-[#ff3b30] transition-colors" />
                </button>
              );
            })}
          </div>

          <p className="text-sm text-white/50 mt-7 text-center">
            Already have access?{" "}
            <Link to="/login" className="text-[#ff3b30] hover:underline" data-testid="go-login-link">
              Sign in
            </Link>
          </p>
        </div>
      </AuthLayout>
    );
  }

  /* ---------- Step 1: explainer ---------- */
  if (step === 1) {
    const items = howToContent(isLender);
    return (
      <AuthLayout>
        <div className="bg-[#141414]/80 backdrop-blur-2xl border border-white/10 rounded-2xl p-8 shadow-2xl">
          <button
            onClick={() => setStep(0)}
            data-testid="explainer-back"
            className="flex items-center gap-1.5 text-sm text-white/50 hover:text-white transition-colors mb-4"
          >
            <ArrowLeft className="h-4 w-4" /> Back
          </button>
          <h1 className="font-display text-2xl tracking-tight">
            {isLender ? "Here's how it makes your life easier" : "Here's how it works — and why you'll love it"}
          </h1>

          <div className="mt-5 space-y-3 max-h-[52vh] overflow-y-auto pr-1" data-testid="signup-explainer">
            {items.map((it, i) => {
              const Icon = it.icon;
              return (
                <div key={i} className="rounded-xl border border-white/10 bg-black/25 p-4">
                  <div className="flex items-center gap-2.5">
                    <div className="h-8 w-8 rounded-lg bg-[#ff3b30]/15 text-[#ff3b30] flex items-center justify-center shrink-0">
                      <Icon className="h-4 w-4" />
                    </div>
                    <p className="font-semibold text-sm">{it.title}</p>
                  </div>
                  {it.body && <p className="text-sm text-white/60 mt-2.5 leading-relaxed">{it.body}</p>}
                  {it.steps && (
                    <ol className="mt-2.5 space-y-2">
                      {it.steps.map((s, idx) => (
                        <li key={idx} className="flex gap-2.5 text-xs text-white/60 leading-relaxed">
                          <span className="h-5 w-5 rounded-full bg-[#ff3b30] text-white text-[10px] font-bold flex items-center justify-center shrink-0">
                            {idx + 1}
                          </span>
                          <span className="pt-0.5">{s}</span>
                        </li>
                      ))}
                    </ol>
                  )}
                </div>
              );
            })}
          </div>

          <div className="mt-6 flex items-center gap-3">
            <button
              onClick={() => setStep(2)}
              data-testid="explainer-skip"
              className="text-sm text-white/45 hover:text-white/70 transition-colors"
            >
              Skip
            </button>
            <Button
              onClick={() => setStep(2)}
              data-testid="explainer-continue"
              className="ml-auto h-11 px-6 bg-[#ff3b30] hover:bg-[#ff5147] text-white font-semibold rounded-xl"
            >
              Let's create my account <ArrowRight className="h-4 w-4 ml-1.5" />
            </Button>
          </div>
        </div>
      </AuthLayout>
    );
  }

  /* ---------- Step 2: form ---------- */
  return (
    <AuthLayout>
      <div className="bg-[#141414]/80 backdrop-blur-2xl border border-white/10 rounded-2xl p-8 shadow-2xl">
        <button
          onClick={() => setStep(1)}
          data-testid="form-back"
          className="flex items-center gap-1.5 text-sm text-white/50 hover:text-white transition-colors mb-4"
        >
          <ArrowLeft className="h-4 w-4" /> Back
        </button>
        <h1 className="font-display text-3xl tracking-tight">
          {isLender ? "Create your lender account" : "Create your realtor account"}
        </h1>
        <p className="text-white/50 text-sm mt-2">
          {isLender ? "30 seconds and you're the hero. 🦸" : "Almost there — send pre-approvals with your thumbs. 👍"}
        </p>

        <form onSubmit={submit} className="mt-6 space-y-5">
          <div className="space-y-2">
            <Label className="text-xs tracking-[0.15em] uppercase text-white/50">Full name</Label>
            <Input required value={form.name} onChange={set("name")} data-testid="signup-name-input" className={inputCls} placeholder={isLender ? "Anthony Lender" : "Jane Realtor"} />
          </div>
          {isLender && (
            <div className="space-y-2">
              <Label className="text-xs tracking-[0.15em] uppercase text-white/50">Company name</Label>
              <Input value={form.company} onChange={set("company")} data-testid="signup-company-input" className={inputCls} placeholder="Acme Home Loans" />
            </div>
          )}
          <div className="space-y-2">
            <Label className="text-xs tracking-[0.15em] uppercase text-white/50">Email</Label>
            <Input required type="email" value={form.email} onChange={set("email")} data-testid="signup-email-input" className={inputCls} placeholder="you@brokerage.com" />
          </div>
          {!isLender && (
            <div className="space-y-2">
              <Label className="text-xs tracking-[0.15em] uppercase text-white/50">Your lender's email</Label>
              <Input type="email" value={form.lender_email} onChange={set("lender_email")} data-testid="signup-lender-email-input" className={inputCls} placeholder="your.lender@email.com" />
              <p className="text-xs text-white/40">We'll invite them and show them how to set you up. If they're already here, you'll link right up. (You can add more lenders later.)</p>
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-xs tracking-[0.15em] uppercase text-white/50">Phone</Label>
              <Input value={form.phone} onChange={set("phone")} data-testid="signup-phone-input" className={inputCls} placeholder="(555) 555-5555" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs tracking-[0.15em] uppercase text-white/50">{isLender ? "NMLS # (optional)" : "License # (optional)"}</Label>
              <Input value={form.license_number} onChange={set("license_number")} data-testid="signup-license-input" className={inputCls} placeholder={isLender ? "NMLS 123456" : "DRE / License #"} />
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-xs tracking-[0.15em] uppercase text-white/50">Password</Label>
            <div className="relative">
              <Input required type={showPw ? "text" : "password"} value={form.password} onChange={set("password")} data-testid="signup-password-input" className={`${inputCls} pr-11`} placeholder="Create a password" />
              <button type="button" onClick={() => setShowPw((v) => !v)} data-testid="signup-toggle-password" aria-label={showPw ? "Hide password" : "Show password"} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/50 hover:text-white transition-colors">
                {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>

          {error && <p className="text-sm text-[#ff3b30]" data-testid="signup-error">{error}</p>}

          <Button type="submit" disabled={loading} data-testid="signup-submit-button" className="w-full h-11 bg-[#ff3b30] hover:bg-[#ff5147] text-white font-semibold rounded-xl transition-colors">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : isLender ? "Create lender account" : "Create realtor account"}
          </Button>
        </form>

        <p className="text-sm text-white/50 mt-6 text-center">
          Already have access?{" "}
          <Link to="/login" className="text-[#ff3b30] hover:underline" data-testid="go-login-link">
            Sign in
          </Link>
        </p>
      </div>
    </AuthLayout>
  );
}
